/**
 * PLW back-in-stock signup form — subscribes an email to a Klaviyo
 * back-in-stock alert for one variant via the client Back In Stock API
 * (same direct client-API pattern as plw-klaviyo-form.js; klaviyo.js not
 * required). The Klaviyo "Back in Stock" flow sends the email when Shopify
 * inventory for the variant returns above the flow's threshold.
 *
 * Wraps a plain <form> with an email input and a submit button:
 *
 *   <plw-bis-form data-company-id data-variant-id>
 *     <form> … <input type="email" required> … </form>
 *     <p data-status="success" hidden>…</p>
 *     <p data-status="error" hidden>…</p>
 *   </plw-bis-form>
 *
 * `data-variant-id` (the numeric Shopify variant id) is stamped by the opener
 * (`openBackInStock` below — called by plw-variant-selector.js's per-value
 * "Notify me" buttons and by the delegated `[data-plw-bis-variant-id]` click
 * handler that serves the "Sold out – Notify me" buy button + sticky bar)
 * before the overlay is shown; the Klaviyo catalog
 * id is derived as `$shopify:::$default:::{variant_id}` — the id format of
 * Shopify-synced Klaviyo catalogs. Status copy is server-rendered in Liquid
 * (translated); this module only toggles `hidden`.
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2026-04-15';

class PlwBisForm extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLFormElement | null} */
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.abortController?.abort();
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.form || !this.dataset.variantId) return;
    const input = this.form.querySelector('input[type="email"]');
    const button = this.form.querySelector('button[type="submit"]');
    if (!(input instanceof HTMLInputElement)) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.#setStatus(null);
    if (button) button.disabled = true;

    try {
      const response = await fetch(
        `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId || '')}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(this.#payload(input.value)),
          signal: this.abortController.signal,
        }
      );
      if (!response.ok) throw new Error(`Klaviyo back-in-stock subscription failed (${response.status})`);
      input.value = '';
      this.#setStatus('success');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#setStatus('error');
      input.focus();
    } finally {
      if (button) button.disabled = false;
    }
  };

  /** @param {string} email */
  #payload(email) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.dataset.variantId}`,
            },
          },
        },
      },
    };
  }

  /** Show one status element (or none) — copy lives in the Liquid. @param {'success' | 'error' | null} state */
  #setStatus(state) {
    for (const status of this.querySelectorAll('[data-status]')) {
      status.hidden = status.dataset.status !== state;
    }
  }
}

if (!customElements.get('plw-bis-form')) {
  customElements.define('plw-bis-form', PlwBisForm);
}

/**
 * Opens the back-in-stock overlay (snippets/plw-bis.liquid) for one variant.
 * Stamps the form directly (dataset + [data-bis-value]) rather than via a
 * method so the call is safe before <plw-bis-form> upgrades.
 * @param {{ variantId: string, value?: string }} options - The Klaviyo/Shopify
 *   variant id and the label shown under the product name ("M", "Ocean / M").
 * @returns {boolean} Whether the overlay was opened.
 */
export function openBackInStock({ variantId, value = '' }) {
  const overlay = document.getElementById('plw-overlay-bis');
  const form = overlay?.querySelector('plw-bis-form');
  if (!variantId || !overlay || !form) return false;

  form.setAttribute('data-variant-id', variantId);
  const label = form.querySelector('[data-bis-value]');
  if (label) label.textContent = value;
  for (const status of form.querySelectorAll('[data-status]')) status.hidden = true;

  /** @type {{ showDialog?: () => void }} */ (overlay).showDialog?.();
  return true;
}

/**
 * Delegated opener for the sold-out buy button: any click on an element
 * carrying `data-plw-bis-variant-id` (+ optional `data-plw-bis-value`) opens
 * the overlay for that variant. Delegated because the buy button is morphed
 * on every variant change (product-form.js), so a per-element listener would
 * not survive; the button itself is `type="button"`, so nothing submits.
 */
document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-plw-bis-variant-id]') : null
  );
  // The sticky bar's button routes through sticky-add-to-cart.js (mobile reveal first).
  if (!trigger || trigger.matches(':disabled') || trigger.closest('sticky-add-to-cart')) return;
  event.preventDefault();
  openBackInStock({ variantId: trigger.dataset.plwBisVariantId ?? '', value: trigger.dataset.plwBisValue });
});
